import React from "react";
import { useUsers } from "../../../../common/CommonQueries";
import { Chip, CircularProgress, TextField, Box, makeStyles, Theme } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import { getTheme } from "../../../../common/getLocalTheme";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  chipContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  userChip: {
    backgroundColor: "#f0f4f8",
    color: "#475569",
    fontWeight: 500,
    fontSize: "0.875rem",
    borderRadius: 16,
    padding: "4px 2px",
    height: "auto",
    "& .MuiChip-label": {
      paddingLeft: 12,
      paddingRight: 6,
      paddingTop: 4,
      paddingBottom: 4,
    },
    "& .MuiChip-deleteIcon": {
      color: "#94a3b8",
      fontSize: 16,
      marginRight: 8,
      "&:hover": {
        color: "#64748b",
      },
    },
  },
  autocompleteInput: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    transition: "border-color 0.2s ease-in-out",
    "&:hover": {
      borderColor: "#cbd5e1",
    },
    "& .MuiOutlinedInput-root": {
      padding: "6px 12px !important",
      borderRadius: 8,
      "& fieldset": {
        border: "none", // Remove default MUI border since outer box has border
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "0.9rem",
      color: "#1e293b",
      "&::placeholder": {
        color: "#16191D",
        opacity: 0.9,
      },
    },
  },
}));

interface TaggedUser {
  id: number;
  username: string;
}

interface TaggedUsersSectionProps {
  taggedUsers?: TaggedUser[];
  setTaggedUsers?: React.Dispatch<React.SetStateAction<TaggedUser[]>> | ((users: any) => void);
  isNew?: boolean;
}

export const TaggedUsersSection: React.FC<TaggedUsersSectionProps> = ({
  taggedUsers = [],
  setTaggedUsers,
}) => {
  const classes = useStyles();
  const [unusedUsers, setUnusedUsers] = React.useState<TaggedUser[]>([]);

  const handleAdd = (user: TaggedUser) => {
    if (setTaggedUsers) {
      if (typeof setTaggedUsers === "function") {
        setTaggedUsers((prev: TaggedUser[]) => [...prev, user]);
      }
    }
  };

  const handleDelete = (userToDelete: TaggedUser) => {
    if (setTaggedUsers) {
      if (typeof setTaggedUsers === "function") {
        setTaggedUsers((prev: TaggedUser[]) =>
          prev.filter((user: TaggedUser) => user.id !== userToDelete.id)
        );
      }
    }
  };

  const { data: users, isLoading: isLoadingUsers } = useUsers();

  React.useEffect(() => {
    if (users && taggedUsers.length !== 0) {
      let array = users;
      taggedUsers.forEach((tUser: TaggedUser) => {
        array = array.filter((user: TaggedUser) => user.id !== tUser.id);
      });
      setUnusedUsers(array);
    } else if (users && taggedUsers.length === 0) {
      setUnusedUsers(users);
    }
  }, [users, taggedUsers]);

  if (isLoadingUsers) {
    return (
      <Box display="flex" alignItems="center" padding={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* List of tagged users as pill chips */}
      <Box className={classes.chipContainer}>
        {taggedUsers?.map((user: TaggedUser) => (
          <Chip
            key={user.id}
            label={user.username}
            disabled={getTheme()}
            onDelete={() => handleDelete(user)}
            deleteIcon={<CloseIcon />}
            className={classes.userChip}
          />
        ))}
      </Box>

      {/* Add User Autocomplete Dropdown */}
      <Autocomplete
        options={unusedUsers || []}
        getOptionLabel={(option: TaggedUser) => option.username}
        disabled={getTheme()}
        onChange={(_, value) => {
          if (value) {
            handleAdd(value);
          }
        }}
        value={null} // Reset selection after item is chosen
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="+ Add a user"
            variant="outlined"
            fullWidth
            className={classes.autocompleteInput}
          />
        )}
      />
    </Box>
  );
};